import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  getTimelineEvent,
  type TimelineEventWithExtras,
} from '@/features/timeline/lib/timelineQueries';
import { updateEvent } from '@/features/timeline/lib/updateEvent';
import { getEventTypeMeta } from '@/features/timeline/lib/eventType';
import { FieldFlagsGroup } from '@/features/timeline/components/flag-chip';
import { PhotoLightbox } from '@/features/timeline/components/photo-lightbox';
import { signedPrescriptionUrl } from '@/features/timeline/lib/upload';
import type { AIFlag, EventType } from '@/types/db';
import { PdfPreview } from '@/components/pdf-preview';
import { supabase } from '@/lib/supabase';

const DATE_FLAG_CODES = new Set([
  'unreadable_date',
  'date_converted_from_hijri',
  'date_assumed_dmy',
  'arabic_indic_digits_normalized',
]);
const DIAGNOSIS_FLAG_CODES = new Set(['chief_complaint_not_diagnosis']);

const FREQ_CHIPS = ['Once daily', 'Twice daily', '3× daily', '4× daily'] as const;
const TIMING_CHIPS = ['Before meals', 'After meals', 'At bedtime', 'As needed'] as const;
type FreqChip = typeof FREQ_CHIPS[number];
type TimingChip = typeof TIMING_CHIPS[number];

const INTERVAL_TO_FREQ: [RegExp, FreqChip][] = [
  [/\b(every|each|q\.?)\s*6\s*(h|hr|hrs|hours?)?\b/, '4× daily'],
  [/\b(every|each|q\.?)\s*8\s*(h|hr|hrs|hours?)?\b/, '3× daily'],
  [/\b(every|each|q\.?)\s*12\s*(h|hr|hrs|hours?)?\b/, 'Twice daily'],
  [/\b(every|each|q\.?)\s*24\s*(h|hr|hrs|hours?)?\b/, 'Once daily'],
  [/\b6\s*hourly\b/, '4× daily'],
  [/\b8\s*hourly\b/, '3× daily'],
  [/\b12\s*hourly\b/, 'Twice daily'],
  // Latin medical abbreviations common on Middle East prescriptions
  [/\bqid\b/, '4× daily'],
  [/\btid\b/, '3× daily'],
  [/\bbid\b/, 'Twice daily'],
  [/\bod\b/, 'Once daily'],
];

function detectFreqChip(frequency: string | null): FreqChip | null {
  if (!frequency) return null;
  const lower = frequency.toLowerCase();
  for (const [pattern, chip] of INTERVAL_TO_FREQ) {
    if (pattern.test(lower)) return chip;
  }
  return FREQ_CHIPS.find((c) => lower.includes(c.toLowerCase())) ?? null;
}

function detectTimingChip(frequency: string | null): TimingChip | null {
  if (!frequency) return null;
  const lower = frequency.toLowerCase();
  return TIMING_CHIPS.find((c) => lower.includes(c.toLowerCase())) ?? null;
}

interface EditMed {
  localId: string;
  drug_name: string;
  dosage: string;
  frequency: string;
  duration_days: string;
  freqChip: FreqChip | null;
  timingChip: TimingChip | null;
}

function makeMedId() {
  return Math.random().toString(36).slice(2);
}

function medsFromEvent(meds: TimelineEventWithExtras['medications']): EditMed[] {
  return meds.map((m) => ({
    localId: m.id,
    drug_name: m.drug_name ?? '',
    dosage: m.dosage ?? '',
    frequency: m.frequency ?? '',
    duration_days: m.duration_days !== null ? String(m.duration_days) : '',
    freqChip: detectFreqChip(m.frequency),
    timingChip: detectTimingChip(m.frequency),
  }));
}

function getDrugContext(flag: AIFlag): string | null {
  const m = flag.message.match(/\(drug: (.+)\)$/);
  return m ? m[1] : null;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; event: TimelineEventWithExtras }
  | { kind: 'missing' }
  | { kind: 'error'; message: string };

const SHOWS_MEDS = new Set<EventType>(['prescription', 'lab_report', 'scan']);
const SHOWS_TITLE = new Set<EventType>(['doctor_visit', 'vaccine', 'other']);
const SHOWS_DIAGNOSIS = new Set<EventType>(['prescription', 'lab_report', 'scan']);

export function EditEventPage() {
  const { id, eventId } = useParams<{ id: string; eventId: string }>();
  const navigate = useNavigate();

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [title, setTitle] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [doctorName, setDoctorName] = useState('');
  const [doctorSpecialty, setDoctorSpecialty] = useState('');
  const [clinicName, setClinicName] = useState('');
  const [clinicPhone, setClinicPhone] = useState('');
  const [clinicAddress, setClinicAddress] = useState('');
  const [insuranceCompany, setInsuranceCompany] = useState('');
  const [insuranceClaim, setInsuranceClaim] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [notes, setNotes] = useState('');
  const [meds, setMeds] = useState<EditMed[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolvedFlagCodes, setResolvedFlagCodes] = useState<Set<string>>(new Set());

  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoErr, setPhotoErr] = useState(false);
  const [photoReloadKey, setPhotoReloadKey] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const retryPhoto = useCallback(() => setPhotoReloadKey((k) => k + 1), []);

  const load = useCallback(async () => {
    if (!id || !eventId) return;
    setState({ kind: 'loading' });
    try {
      const event = await getTimelineEvent(id, eventId);
      if (!event) {
        setState({ kind: 'missing' });
        return;
      }
      setState({ kind: 'ready', event });
      setTitle(event.title ?? '');
      setEventDate(event.event_date ?? '');
      setDoctorName(event.doctor_name ?? '');
      setDoctorSpecialty(event.doctor_specialty ?? '');
      setClinicName(event.clinic_name ?? '');
      setClinicPhone(event.clinic_phone ?? '');
      setClinicAddress(event.clinic_address ?? '');
      setInsuranceCompany(event.insurance?.company ?? '');
      setInsuranceClaim(event.insurance?.claim_number ?? '');
      setDiagnosis(event.diagnosis ?? '');
      setNotes(event.notes ?? '');
      setMeds(medsFromEvent(event.medications));
      setResolvedFlagCodes(
        new Set(
          (event.ai_flags ?? [])
            .filter((f: AIFlag) => !!f.resolved_at)
            .map((f: AIFlag) => f.code),
        ),
      );
    } catch (err) {
      console.error('[edit-event] load failed', err);
      setState({ kind: 'error', message: "Couldn't load this record." });
    }
  }, [id, eventId]);

  useEffect(() => {
    load();
  }, [load]);

  const attachmentPath =
    state.kind === 'ready' ? (state.event.attachments?.[0]?.storage_path ?? null) : null;
  const attachmentFileType =
    state.kind === 'ready' ? (state.event.attachments?.[0]?.file_type ?? null) : null;
  const attachmentFileName =
    state.kind === 'ready' ? (state.event.attachments?.[0]?.file_name ?? null) : null;

  useEffect(() => {
    if (!attachmentPath) return;
    let cancelled = false;
    setPhotoErr(false);
    setPhotoUrl(null);
    signedPrescriptionUrl(attachmentPath)
      .then((url) => { if (!cancelled) setPhotoUrl(url); })
      .catch((err) => {
        console.error('[edit-event] signed url failed', err);
        if (!cancelled) setPhotoErr(true);
      });
    return () => { cancelled = true; };
  }, [attachmentPath, photoReloadKey]);

  function addMed() {
    setMeds((prev) => [
      ...prev,
      { localId: makeMedId(), drug_name: '', dosage: '', frequency: '', duration_days: '', freqChip: null, timingChip: null },
    ]);
  }

  function removeMed(localId: string) {
    setMeds((prev) => prev.filter((m) => m.localId !== localId));
  }

  function updateMed(localId: string, patch: Partial<Omit<EditMed, 'localId'>>) {
    setMeds((prev) => prev.map((m) => m.localId === localId ? { ...m, ...patch } : m));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (state.kind !== 'ready' || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await updateEvent({
        profileId: state.event.profile_id,
        eventId: state.event.id,
        title: title.trim() || null,
        eventDate: eventDate || null,
        doctorName: doctorName.trim() || null,
        doctorSpecialty: doctorSpecialty.trim() || null,
        clinicName: clinicName.trim() || null,
        clinicPhone: clinicPhone.trim() || null,
        clinicAddress: clinicAddress.trim() || null,
        insuranceCompany: insuranceCompany.trim() || null,
        insuranceClaim: insuranceClaim.trim() || null,
        diagnosis: diagnosis.trim() || null,
        notes: notes.trim() || null,
        medications: meds
          .filter((m) => m.drug_name.trim())
          .map((m) => ({
            drug_name: m.drug_name.trim() || null,
            dosage: m.dosage.trim() || null,
            frequency: m.frequency.trim() || null,
            duration_days: m.duration_days
              ? parseInt(m.duration_days, 10) || null
              : null,
          })),
      });

      if (resolvedFlagCodes.size > 0) {
        const now = new Date().toISOString();
        const updatedFlags = (state.event.ai_flags ?? []).map((f: AIFlag) =>
          resolvedFlagCodes.has(f.code) && !f.resolved_at
            ? { ...f, resolved_at: now }
            : f,
        );
        const { error: flagErr } = await supabase
          .from('timeline_events')
          .update({ ai_flags: updatedFlags })
          .eq('id', state.event.id);
        if (flagErr) {
          console.error('[edit-event] flag save failed', flagErr);
        }
      }

      navigate(`/profile/${id}`, {
        replace: true,
        state: { justSavedEventId: state.event.id, toast: 'Record updated.' },
      });
    } catch (err) {
      console.error('[edit-event] save failed', err);
      setError("Couldn't save. Please try again.");
      setSubmitting(false);
    }
  }

  const backLink = id ? `/profile/${id}` : '/';
  const eventType = state.kind === 'ready' ? state.event.event_type : null;
  const showMeds = eventType ? SHOWS_MEDS.has(eventType) : false;
  const showTitle = eventType ? SHOWS_TITLE.has(eventType) : false;
  const showDiagnosis = eventType ? SHOWS_DIAGNOSIS.has(eventType) : false;

  const allFlags: AIFlag[] = state.kind === 'ready' ? (state.event.ai_flags ?? []) : [];
  const dateFlags = allFlags.filter((f) => DATE_FLAG_CODES.has(f.code));
  const diagnosisFlags = allFlags.filter((f) => DIAGNOSIS_FLAG_CODES.has(f.code));
  const generalFlags = allFlags.filter(
    (f) => !DATE_FLAG_CODES.has(f.code) && !DIAGNOSIS_FLAG_CODES.has(f.code),
  );
  const eventLevelFlags = generalFlags.filter((f) => getDrugContext(f) === null);
  const handleFlagResolve = (code: string) =>
    setResolvedFlagCodes((prev) => new Set([...prev, code]));

  const hasAttachment = !!attachmentPath;

  return (
    <main className="min-h-screen safe-top pb-32">
      <header className="sticky top-0 z-10 border-b border-border bg-surface/95 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <Link
            to={backLink}
            aria-label="Back"
            className="h-10 w-10 -ml-1 rounded-full flex items-center justify-center text-ink-muted hover:text-ink active:bg-canvas shrink-0"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </Link>
          <h1 className="text-lg font-bold truncate flex-1">Edit record</h1>
        </div>
      </header>

      <div className={`max-w-3xl mx-auto px-4 py-4${hasAttachment ? ' md:grid md:grid-cols-2 md:gap-6 md:items-start' : ''}`}>
        {hasAttachment && (
          <section className="mb-4 md:mb-0">
            <div className="card overflow-hidden">
              {photoUrl && !photoErr ? (
                attachmentFileType === 'application/pdf' ? (
                  <PdfPreview
                    url={photoUrl}
                    fileName={attachmentFileName}
                    variant="full"
                    helperText="Open the PDF to cross-check each field below."
                  />
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setLightboxOpen(true)}
                      aria-label="Tap to zoom the prescription photo"
                      className="block w-full"
                    >
                      <img
                        src={photoUrl}
                        alt="Prescription"
                        className="w-full h-auto max-h-[60vh] object-contain bg-canvas"
                      />
                    </button>
                    <p className="text-xs text-ink-muted px-3 py-2 border-t border-border bg-canvas">
                      Tap the photo to zoom. Check each field below matches the paper.
                    </p>
                  </>
                )
              ) : (
                <div className="flex flex-col items-center justify-center h-48 text-ink-muted text-sm gap-3">
                  {photoErr ? (
                    <>
                      <p>Photo couldn't load.</p>
                      <button type="button" onClick={retryPhoto} className="btn-secondary">
                        Try again
                      </button>
                    </>
                  ) : (
                    <p>Loading photo…</p>
                  )}
                </div>
              )}
            </div>
          </section>
        )}

        <section>
          {state.kind === 'loading' && (
            <div className="card p-6 h-48 animate-pulse bg-canvas" aria-busy="true" />
          )}

          {state.kind === 'missing' && (
            <div className="card p-6 text-center">
              <p className="font-medium mb-2">Record not found</p>
              <Link to={backLink} className="btn-secondary inline-flex">
                Back
              </Link>
            </div>
          )}

          {state.kind === 'error' && (
            <div className="card p-6 text-center">
              <p className="text-flag-block mb-3">{state.message}</p>
              <button type="button" onClick={load} className="btn-secondary">
                Try again
              </button>
            </div>
          )}

          {state.kind === 'ready' && (
            <form id="edit-event-form" onSubmit={handleSubmit} className="card p-5 space-y-4">
              {(() => {
                const meta = getEventTypeMeta(state.event.event_type);
                return (
                  <div
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${meta.pillBg} ${meta.pillText}`}
                  >
                    <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
                    {meta.label}
                  </div>
                );
              })()}

              {eventLevelFlags.length > 0 && (
                <FieldFlagsGroup
                  flags={eventLevelFlags}
                  resolvedCodes={resolvedFlagCodes}
                  onResolve={handleFlagResolve}
                />
              )}

              {showTitle && (
                <div>
                  <label className="label" htmlFor="edit-title">
                    Title
                  </label>
                  <input
                    id="edit-title"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="input"
                    maxLength={140}
                  />
                </div>
              )}

              <div>
                <label className="label" htmlFor="edit-date">
                  Date
                </label>
                <input
                  id="edit-date"
                  type="date"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  className="input"
                />
                <FieldFlagsGroup
                  flags={dateFlags}
                  resolvedCodes={resolvedFlagCodes}
                  onResolve={handleFlagResolve}
                />
              </div>

              <div>
                <label className="label" htmlFor="edit-doctor">
                  Doctor{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <input
                  id="edit-doctor"
                  type="text"
                  value={doctorName}
                  onChange={(e) => setDoctorName(e.target.value)}
                  className="input"
                  placeholder="e.g. Dr. Sarah Ahmed"
                  maxLength={200}
                />
              </div>

              <div>
                <label className="label" htmlFor="edit-specialty">
                  Specialty{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <input
                  id="edit-specialty"
                  type="text"
                  value={doctorSpecialty}
                  onChange={(e) => setDoctorSpecialty(e.target.value)}
                  className="input"
                  placeholder="e.g. Pediatrics, Cardiology"
                  maxLength={100}
                />
              </div>

              <div>
                <label className="label" htmlFor="edit-clinic">
                  Clinic or hospital{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <input
                  id="edit-clinic"
                  type="text"
                  value={clinicName}
                  onChange={(e) => setClinicName(e.target.value)}
                  className="input"
                  placeholder="e.g. Cairo Medical Center"
                  maxLength={200}
                />
              </div>

              <div>
                <label className="label" htmlFor="edit-clinic-phone">
                  Phone{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <input
                  id="edit-clinic-phone"
                  type="tel"
                  value={clinicPhone}
                  onChange={(e) => setClinicPhone(e.target.value)}
                  className="input"
                  placeholder="e.g. +20 2 1234 5678"
                  maxLength={50}
                />
              </div>

              <div>
                <label className="label" htmlFor="edit-clinic-address">
                  Address{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <input
                  id="edit-clinic-address"
                  type="text"
                  value={clinicAddress}
                  onChange={(e) => setClinicAddress(e.target.value)}
                  className="input"
                  placeholder="e.g. 15 Tahrir Square, Cairo"
                  maxLength={300}
                />
              </div>

              <div>
                <label className="label" htmlFor="edit-insurance-company">
                  Insurance company{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <input
                  id="edit-insurance-company"
                  type="text"
                  value={insuranceCompany}
                  onChange={(e) => setInsuranceCompany(e.target.value)}
                  className="input"
                  placeholder="e.g. GlobeMed, MetLife"
                  maxLength={200}
                />
              </div>

              <div>
                <label className="label" htmlFor="edit-insurance-claim">
                  Claim / policy #{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <input
                  id="edit-insurance-claim"
                  type="text"
                  value={insuranceClaim}
                  onChange={(e) => setInsuranceClaim(e.target.value)}
                  className="input"
                  placeholder="e.g. CLM-20240412-001"
                  maxLength={100}
                />
              </div>

              {showDiagnosis && (
                <div>
                  <label className="label" htmlFor="edit-diagnosis">
                    Diagnosis{' '}
                    <span className="text-ink-subtle font-normal">(optional)</span>
                  </label>
                  <input
                    id="edit-diagnosis"
                    type="text"
                    value={diagnosis}
                    onChange={(e) => setDiagnosis(e.target.value)}
                    className="input"
                    maxLength={500}
                  />
                  <FieldFlagsGroup
                    flags={diagnosisFlags}
                    resolvedCodes={resolvedFlagCodes}
                    onResolve={handleFlagResolve}
                  />
                </div>
              )}

              <div>
                <label className="label" htmlFor="edit-notes">
                  Notes{' '}
                  <span className="text-ink-subtle font-normal">(optional)</span>
                </label>
                <textarea
                  id="edit-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="input min-h-[88px] resize-y"
                  maxLength={2000}
                />
              </div>

              {showMeds && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="label mb-0">Medications</span>
                    <button
                      type="button"
                      onClick={addMed}
                      className="text-sm text-accent hover:text-accent-hover font-medium"
                    >
                      + Add
                    </button>
                  </div>
                  <ul className="space-y-3">
                    {meds.map((med, idx) => {
                      const medKey = med.drug_name.trim() || `drug ${idx + 1}`;
                      const medFlags = generalFlags.filter((f) => getDrugContext(f) === medKey);
                      return (
                      <li
                        key={med.localId}
                        className="rounded-lg border border-border border-l-4 border-l-evt-rx-rail p-3 bg-canvas"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-medium text-evt-rx-ink">
                            Medication {idx + 1}
                          </p>
                          {meds.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeMed(med.localId)}
                              aria-label={`Remove medication ${idx + 1}`}
                              className="h-11 w-11 -m-2 rounded-full text-ink-muted hover:text-flag-block active:bg-canvas flex items-center justify-center"
                            >
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="h-5 w-5"
                                aria-hidden="true"
                              >
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" />
                                <path d="M10 11v6" />
                                <path d="M14 11v6" />
                                <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                              </svg>
                            </button>
                          )}
                        </div>
                        {medFlags.length > 0 && (
                          <div className="mb-2">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle mb-1">AI notes</p>
                            <FieldFlagsGroup
                              flags={medFlags}
                              resolvedCodes={resolvedFlagCodes}
                              onResolve={handleFlagResolve}
                            />
                          </div>
                        )}
                        <input
                          type="text"
                          placeholder="Drug name"
                          value={med.drug_name}
                          onChange={(e) => updateMed(med.localId, { drug_name: e.target.value })}
                          className="input mb-3"
                          maxLength={200}
                        />

                        <div className="mb-3">
                          <p className="text-xs font-medium text-ink-muted mb-1">Dose</p>
                          <input
                            type="text"
                            placeholder="e.g. 1 capsule, 10 mg"
                            value={med.dosage}
                            onChange={(e) => updateMed(med.localId, { dosage: e.target.value })}
                            className="input"
                            maxLength={100}
                          />
                        </div>

                        <div className="mb-3">
                          <p className="text-xs font-medium text-ink-muted mb-1">Schedule</p>
                          <input
                            type="text"
                            placeholder="e.g. twice daily after meals"
                            value={med.frequency}
                            onChange={(e) =>
                              updateMed(med.localId, {
                                frequency: e.target.value,
                                freqChip: detectFreqChip(e.target.value || null),
                                timingChip: detectTimingChip(e.target.value || null),
                              })
                            }
                            className="input mb-2"
                            maxLength={100}
                          />
                          <div className="grid grid-cols-4 gap-1.5">
                            {FREQ_CHIPS.map((chip) => (
                              <button
                                key={chip}
                                type="button"
                                onClick={() => {
                                  const next = med.freqChip === chip ? null : chip;
                                  const composed = [next, med.timingChip].filter(Boolean).join(' ') || '';
                                  updateMed(med.localId, { freqChip: next, frequency: composed });
                                }}
                                className={`h-11 rounded-lg text-xs font-medium border transition-colors leading-none ${
                                  med.freqChip === chip
                                    ? 'bg-accent text-white border-accent'
                                    : 'bg-surface border-border text-ink-muted hover:border-accent hover:text-accent active:bg-canvas'
                                }`}
                              >
                                {chip}
                              </button>
                            ))}
                          </div>
                          <div className="grid grid-cols-4 gap-1.5 mt-1.5">
                            {TIMING_CHIPS.map((chip) => (
                              <button
                                key={chip}
                                type="button"
                                onClick={() => {
                                  const next = med.timingChip === chip ? null : chip;
                                  const composed = [med.freqChip, next].filter(Boolean).join(' ') || '';
                                  updateMed(med.localId, { timingChip: next, frequency: composed });
                                }}
                                className={`h-11 rounded-lg text-xs font-medium border transition-colors leading-none px-1 ${
                                  med.timingChip === chip
                                    ? 'bg-accent text-white border-accent'
                                    : 'bg-surface border-border text-ink-muted hover:border-accent hover:text-accent active:bg-canvas'
                                }`}
                              >
                                {chip}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <p className="text-xs font-medium text-ink-muted whitespace-nowrap">Period of treatment</p>
                          <input
                            type="number"
                            placeholder="—"
                            value={med.duration_days}
                            min={0}
                            max={3650}
                            onChange={(e) => updateMed(med.localId, { duration_days: e.target.value })}
                            className="input w-20 text-center"
                          />
                          <span className="text-xs text-ink-muted">days</span>
                        </div>
                      </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {error && (
                <p className="text-sm text-flag-block" role="alert">
                  {error}
                </p>
              )}
            </form>
          )}
        </section>
      </div>

      <div className="fixed bottom-0 left-0 right-0 safe-bottom bg-surface border-t border-border px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-2">
          <Link to={backLink} className="btn-secondary px-4">Cancel</Link>
          <button
            type="submit"
            form="edit-event-form"
            disabled={submitting || state.kind !== 'ready'}
            className="btn-primary flex-1"
          >
            {submitting ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {lightboxOpen && photoUrl && (
        <PhotoLightbox
          src={photoUrl}
          alt="Prescription"
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </main>
  );
}
